import { useCallback, useMemo, useState } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text, Banner, Card } from "@src/components";
import { CameraThumbnailCard, cameraThumbnailGridStyles } from "@src/features/cameras/components/CameraThumbnailCard";
import { useSiteCameraKeys } from "@src/features/cameras/hooks/useSiteCameraKeys";
import { getCameraKeyString } from "@src/utils/key-parser";
import {
  deriveSuggestionLabel,
  formatRoutineForDisplay,
  getSuggestionAction,
} from "@src/features/alarms/utils/format-routine";
import type { AlarmRoutine } from "@src/services/api/types";
import { FloatingSelectionBar } from "@src/features/settings/components/FloatingSelectionBar";
import { useAlarmsByCameras, useRoutineSuggestions } from "@src/services/api/hooks";
import { RightDrawer } from "@src/components/web/RightDrawer.web";
import { ChangeRoutineContent } from "@src/features/alarms/components/ChangeRoutineContent";
import type { RoutineSuggestion } from "@src/services/api/types";
import { groupCamerasByRoutine, RoutineGroupHeader, useRoutineMap, type RoutineGroup } from "./routine-shared";

export default function ManageRoutines() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const router = useRouter();
  const { siteCameras, cameraKeys, selectedSiteId } = useSiteCameraKeys();
  const effectiveSiteId = selectedSiteId ?? "";

  const [selectedCameraKeys, setSelectedCameraKeys] = useState<Set<string>>(new Set());
  const [drawerVisible, setDrawerVisible] = useState(false);
  const alarmQueries = useAlarmsByCameras(effectiveSiteId, cameraKeys);

  const isLoadingAlarms = alarmQueries.some((q) => q.isLoading);

  const routineMap = useRoutineMap(siteCameras, alarmQueries);

  const routineGroups = useMemo(() => groupCamerasByRoutine(siteCameras, routineMap), [siteCameras, routineMap]);

  // Change routine drawer data
  const selectedCameraKeysArray = useMemo(() => Array.from(selectedCameraKeys), [selectedCameraKeys]);
  const selectedAlarmQueries = useAlarmsByCameras(effectiveSiteId, selectedCameraKeysArray);
  const firstSelectedKey = selectedCameraKeysArray[0];
  const suggestionsQuery = useRoutineSuggestions(effectiveSiteId, firstSelectedKey);

  const isDrawerLoading = selectedAlarmQueries.some((q) => q.isLoading) || suggestionsQuery.isLoading;

  const selectedAlarmSettings = selectedAlarmQueries.map((q) => q.data?.settings);
  const selectedSettingsFingerprint = selectedAlarmSettings
    .map((s) => (s?.routine ? JSON.stringify(s.routine) : ""))
    .join("|");

  const currentRoutinesForDrawer = useMemo(() => {
    const seen = new Map<string, AlarmRoutine>();
    for (const settings of selectedAlarmSettings) {
      const routine = settings?.routine;
      if (routine) {
        const displayKey = formatRoutineForDisplay(routine)
          .map((w) => `${w.name}:${w.timeRanges}`)
          .join("|");
        if (!seen.has(displayKey)) {
          seen.set(displayKey, routine);
        }
      }
    }
    return Array.from(seen.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSettingsFingerprint]);

  const processedSuggestions = useMemo((): (RoutineSuggestion & { action: "armed" | "disarmed" })[] => {
    const suggestions = suggestionsQuery.data;
    if (!suggestions || !Array.isArray(suggestions)) return [];
    return (suggestions as RoutineSuggestion[]).map((suggestion) => ({
      ...suggestion,
      action: getSuggestionAction(suggestion),
      name: deriveSuggestionLabel(suggestion),
    }));
  }, [suggestionsQuery.data]);

  const handleEditRoutine = useCallback(
    (routine?: AlarmRoutine) => {
      router.push({
        pathname: "/alarm-settings/edit-routine",
        params: {
          cameraKeys: JSON.stringify(selectedCameraKeysArray),
          ...(routine ? { routine: encodeURIComponent(JSON.stringify(routine)) } : {}),
        },
      });
    },
    [selectedCameraKeysArray, router],
  );

  const toggleCamera = useCallback((cameraKey: string) => {
    setSelectedCameraKeys((prev) => {
      const next = new Set(prev);
      if (next.has(cameraKey)) {
        next.delete(cameraKey);
      } else {
        next.add(cameraKey);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((group: RoutineGroup) => {
    const groupKeys = group.cameras.map((c) => getCameraKeyString(c));
    setSelectedCameraKeys((prev) => {
      const allSelected = groupKeys.every((key) => prev.has(key));
      const next = new Set(prev);
      if (allSelected) {
        groupKeys.forEach((key) => next.delete(key));
      } else {
        groupKeys.forEach((key) => next.add(key));
      }
      return next;
    });
  }, []);

  const handleChangeRoutine = useCallback(() => {
    if (selectedCameraKeys.size === 0) return;
    setDrawerVisible(true);
  }, [selectedCameraKeys]);

  return (
    <>
      <ScrollView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        contentContainerStyle={{
          paddingHorizontal: theme.spacing["3xl"],
          paddingTop: theme.spacing["4xl"],
          paddingBottom: bottom + theme.spacing["6xl"],
          flexGrow: 1,
        }}
        showsVerticalScrollIndicator={false}
      >
        <Banner title="Select from the cameras below to change their routines." variant="info" />

        {isLoadingAlarms && (
          <View mt="7xl" alignItems="center">
            <ActivityIndicator color={theme.colors.text} />
          </View>
        )}

        <View flex={1} flexDirection="row" style={{ flexWrap: "wrap" }} gap="3xl" mt="3xl">
          {!isLoadingAlarms &&
            routineGroups.map((group) => {
              const groupKeys = group.cameras.map((c) => getCameraKeyString(c));
              const allSelected = groupKeys.every((key) => selectedCameraKeys.has(key));

              return (
                <Card key={group.key} style={{ width: "calc(50% - 8px)" as unknown as number }}>
                  <RoutineGroupHeader
                    routine={group.routine}
                    allSelected={allSelected}
                    onSelectAll={() => toggleSelectAll(group)}
                  />
                  <View style={cameraThumbnailGridStyles.grid} mt="4xl">
                    {group.cameras.map((camera) => {
                      const cameraKey = getCameraKeyString(camera);
                      const isSelected = selectedCameraKeys.has(cameraKey);

                      return (
                        <View key={cameraKey} style={cameraThumbnailGridStyles.gridItem}>
                          <CameraThumbnailCard
                            camera={camera}
                            isSelected={isSelected}
                            onToggle={() => toggleCamera(cameraKey)}
                          />
                        </View>
                      );
                    })}
                  </View>
                </Card>
              );
            })}
        </View>
      </ScrollView>
      <FloatingSelectionBar
        selectedCount={selectedCameraKeys.size}
        actionLabel="Change routine"
        onAction={handleChangeRoutine}
        bottomInset={bottom}
        style={{
          bottom: bottom + theme.spacing["3xl"],
          width: 400,
          left: "50%",
          transform: [{ translateX: "-50%" }],
        }}
      />
      <RightDrawer visible={drawerVisible} onClose={() => setDrawerVisible(false)} title="Change routine" width={450}>
        <ChangeRoutineContent
          backgroundColor={theme.colors.surface}
          isLoading={isDrawerLoading}
          currentRoutines={currentRoutinesForDrawer}
          suggestions={processedSuggestions}
          onEdit={handleEditRoutine}
          bottomInset={0}
        />
      </RightDrawer>
    </>
  );
}
